import type { Landmark, PoseFrame, Side } from '../types.js';
import { LM } from '../pose/landmarks.js';

/**
 * Angle (degrees) at vertex `b` formed by the segments b→a and b→c.
 * Computed in 3D using normalized landmark coordinates (x, y, z).
 *
 * Returns NaN when any segment has zero length.
 */
export function angleBetweenDeg(a: Landmark, b: Landmark, c: Landmark): number {
  const ux = a.x - b.x;
  const uy = a.y - b.y;
  const uz = a.z - b.z;
  const vx = c.x - b.x;
  const vy = c.y - b.y;
  const vz = c.z - b.z;
  const dot = ux * vx + uy * vy + uz * vz;
  const lenU = Math.hypot(ux, uy, uz);
  const lenV = Math.hypot(vx, vy, vz);
  if (lenU === 0 || lenV === 0) return Number.NaN;
  // Numerical clamp before acos.
  const cos = Math.max(-1, Math.min(1, dot / (lenU * lenV)));
  return (Math.acos(cos) * 180) / Math.PI;
}

/** Elbow flexion angle (shoulder-elbow-wrist) in degrees. 180° = straight arm. */
export function elbowAngleDeg(frame: PoseFrame, side: Side): number {
  const [shoulder, elbow, wrist] =
    side === 'right'
      ? [frame[LM.RIGHT_SHOULDER], frame[LM.RIGHT_ELBOW], frame[LM.RIGHT_WRIST]]
      : [frame[LM.LEFT_SHOULDER], frame[LM.LEFT_ELBOW], frame[LM.LEFT_WRIST]];
  if (!shoulder || !elbow || !wrist) return Number.NaN;
  return angleBetweenDeg(shoulder, elbow, wrist);
}

/** Knee flexion angle (hip-knee-ankle) in degrees. */
export function kneeAngleDeg(frame: PoseFrame, side: Side): number {
  const [hip, knee, ankle] =
    side === 'right'
      ? [frame[LM.RIGHT_HIP], frame[LM.RIGHT_KNEE], frame[LM.RIGHT_ANKLE]]
      : [frame[LM.LEFT_HIP], frame[LM.LEFT_KNEE], frame[LM.LEFT_ANKLE]];
  if (!hip || !knee || !ankle) return Number.NaN;
  return angleBetweenDeg(hip, knee, ankle);
}

/** Shoulder abduction angle (elbow-shoulder-hip) in degrees. */
export function shoulderAngleDeg(frame: PoseFrame, side: Side): number {
  const [elbow, shoulder, hip] =
    side === 'right'
      ? [frame[LM.RIGHT_ELBOW], frame[LM.RIGHT_SHOULDER], frame[LM.RIGHT_HIP]]
      : [frame[LM.LEFT_ELBOW], frame[LM.LEFT_SHOULDER], frame[LM.LEFT_HIP]];
  if (!elbow || !shoulder || !hip) return Number.NaN;
  return angleBetweenDeg(elbow, shoulder, hip);
}

/** Compute a per-frame elbow-angle series for the given dominant side. */
export function elbowAngleSeriesDeg(
  frames: readonly PoseFrame[],
  side: Side,
): number[] {
  return frames.map((f) => elbowAngleDeg(f, side));
}
